// Phase C.7a — MANUAL TEAM TESTING STATION. Pure jersey-number/name
// resolution over an already-loaded Team roster (rosterService.
// listTeamRoster() rows) — no fetch, no RPC, no React. This is the
// "identity provider" boundary the architecture review recommended:
// today only jersey-number and name-search exist, but a future identity
// provider (QR, RFID, device assignment) only needs to produce the same
// return shape — an array of team_athletes rows — for the confirmation
// step downstream to stay unchanged.
//
// CRITICAL RULE (C.6/C.7a): jersey number is a FAST LOOKUP KEY ONLY,
// never canonical identity — duplicates are explicitly allowed (047 has
// no uniqueness constraint on it), so an exact jersey match may
// legitimately return MORE than one candidate. This module never picks
// one automatically; the caller must present an explicit disambiguation
// + confirmation step (see TeamTestingStation.js) every time, even when
// there happens to be exactly one match.
//
// Only ACTIVE roster rows (team_athletes.status === 'active', athlete
// not archived) are ever offered as candidates — mirroring every other
// roster-facing surface in this app (TeamRosterPage.js's own
// `activeRoster` filter). An athlete without a jersey number on file
// remains fully findable via matchByName() — never a dead end.

function isSelectable(row) {
  return row.status === 'active' && row.athlete?.status !== 'archived';
}

/** Exact jersey-number match against the active roster. Returns an
 * array — 0, 1, or MORE THAN 1 row (never assumes a number is unique). */
export function matchByJerseyNumber(rosterRows, jerseyNumber) {
  const n = Number(jerseyNumber);
  if (jerseyNumber === '' || jerseyNumber === null || jerseyNumber === undefined || !Number.isFinite(n)) return [];
  return (rosterRows || []).filter(row => isSelectable(row) && row.jersey_number === n);
}

/** Case-insensitive substring match against first/last/full name — the
 * required fallback for an athlete with no jersey number on file, or
 * when the operator doesn't know/remember it. */
export function matchByName(rosterRows, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  return (rosterRows || []).filter(row => {
    if (!isSelectable(row)) return false;
    const first = (row.athlete?.first_name || '').toLowerCase();
    const last = (row.athlete?.last_name || '').toLowerCase();
    return first.includes(q) || last.includes(q) || `${first} ${last}`.includes(q);
  });
}
